/**
 * @fileoverview Info Banner Component
 * 
 * A reusable banner component for displaying informational messages with an icon.
 * Used across the application to show alerts, notifications, or important information.
 * 
 * Features:
 * - Customizable message text
 * - Optional custom icon
 * - Customizable styling via style prop
 * - Consistent styling with application design
 */

import React, { CSSProperties } from 'react';

/**
 * InfoBanner Component Props
 */
interface InfoBannerProps {
  /** The message text to display in the banner */
  message: string;
  /** Optional custom icon source path. Defaults to Info.svg */
  iconSrc?: string;
  /** Optional custom styles for the banner container */
  style?: CSSProperties;
}

/**
 * Info Banner Component
 * 
 * Displays an informational message with an icon in a styled banner container.
 * 
 * @param {InfoBannerProps} props - Component props
 * @returns {JSX.Element} The info banner component
 */
const InfoBanner: React.FC<InfoBannerProps> = ({ 
  message, 
  iconSrc = './../assets/img/Info.svg',
  style 
}) => {
  const defaultStyle: CSSProperties = {
    display: 'flex',
    padding: '16px',
    alignItems: 'flex-start',
    gap: '10px',
    alignSelf: 'stretch',
    borderRadius: '8px',
    border: '1px solid #CFD1D6',
    background: '#EAEBEC'
  };

  return (
    <div style={{ ...defaultStyle, ...style }}>
      <img src={iconSrc} alt="" />
      <span>{message}</span>
    </div>
  );
};

export default InfoBanner;
